import React, { useReducer, useEffect } from "react"
import classNames from "classnames"
import PropTypes from "prop-types"
import CoreFilters from "./CoreFilters"
import AdvancedFilters from "./AdvancedFilters"
import Button from "../../Button/Button"
import { ReactComponent as ArrowTopIcon } from "../../../images/icons/arrow-top.svg"
import { reducer, initState } from "./useReducer/reducer"
import {
  toggleCollapse,
  setWholeState,
  setMode,
  resetForm,
} from "./useReducer/actionCreators"
import {
  CORE,
  ADVANCED,
  countAppliedFilters,
  toSearchParams,
} from "./filterConfig"
import styles from "./index.module.scss"

const MOBILE_QUERY = "(max-width: 768px)"

const TABS = [
  {
    id: CORE,
    label: "Core Filters",
    subtitle: "Core filters for a fast, relevant search",
    note: [
      "The essential filters to help you quickly find relevant resources.",
    ],
  },
  {
    id: ADVANCED,
    label: "Advanced Filters",
    subtitle: "Advanced filters for a more personalized search",
    note: [
      "Advanced filters to refine your results and get more personalized recommendations.",
    ],
  },
]

// Expanded on desktop, collapsed on mobile.
const initCollapse = (state) => {
  try {
    return { ...state, isCollapse: window.matchMedia(MOBILE_QUERY).matches }
  } catch (e) {
    return state
  }
}

const ResourceSearchFilter = ({
  classes,
  searchParamGroup,
  onReset,
  onSubmit,
  onModeChange,
}) => {
  const [state, dispatch] = useReducer(reducer, initState, initCollapse)
  useEffect(() => {
    dispatch(setWholeState(searchParamGroup))
  }, [searchParamGroup])

  const { mode, values } = state
  const activeTab = TABS.find((tab) => tab.id === mode)
  const selectedCount = countAppliedFilters(mode, values)
  const selectMode = (nextMode) => {
    if (nextMode === mode) return
    dispatch(setMode(nextMode))
    onModeChange(nextMode)
  }

  return (
    <section
      className={classNames(styles.filter, classes, {
        [styles.isCollapse]: state.isCollapse,
        [styles.advanced]: mode === ADVANCED,
      })}
      aria-label="Resource filters"
    >
      <div className={styles.header}>
        <p className={styles.subtitle}>{activeTab.subtitle}</p>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={!state.isCollapse}
          onClick={() => dispatch(toggleCollapse())}
        >
          All Filters
          <ArrowTopIcon className={styles.toggleIcon} />
        </button>
      </div>
      <div className={styles.form}>
        <div className={styles.tabs} role="tablist" aria-label="Filter type">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`filter-tab-${tab.id}`}
              aria-selected={mode === tab.id}
              aria-controls="filter-tabpanel"
              className={classNames(styles.tab, {
                [styles.activeTab]: mode === tab.id,
              })}
              onClick={() => selectMode(tab.id)}
            >
              {tab.label}
              {mode === tab.id && selectedCount > 0 && (
                <span className={styles.tabCount}>{selectedCount}</span>
              )}
            </button>
          ))}
        </div>
        <div className={styles.card}>
          <div
            className={styles.body}
            role="tabpanel"
            id="filter-tabpanel"
            aria-labelledby={`filter-tab-${mode}`}
          >
            {mode === CORE ? (
              <CoreFilters values={values} dispatch={dispatch} />
            ) : (
              <AdvancedFilters values={values} dispatch={dispatch} />
            )}
          </div>
          <div className={styles.footer}>
            <Button
              buttonStyle="outlined"
              className={styles.clearBtn}
              onClick={() => {
                dispatch(resetForm())
                onReset()
              }}
            >
              Clear All
            </Button>
            <Button
              className={styles.applyBtn}
              onClick={() => onSubmit(toSearchParams(mode, values))}
            >
              Apply Filters
            </Button>
          </div>
        </div>
        <div className={styles.note}>
          <p>
            {activeTab.note.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </p>
        </div>
      </div>
    </section>
  )
}

ResourceSearchFilter.propTypes = {
  classes: PropTypes.string,
  searchParamGroup: PropTypes.object.isRequired,
  /** Clears every applied filter */
  onReset: PropTypes.func.isRequired,
  /** Receives the search params of the active tab only */
  onSubmit: PropTypes.func.isRequired,
  /** Called when switching between Core and Advanced */
  onModeChange: PropTypes.func,
}

ResourceSearchFilter.defaultProps = {
  onModeChange: () => {},
}

export default ResourceSearchFilter
